import {FlowNodeData, FlowNodeTypes, SettingParamTypes} from "@/types/node";
import {Node} from "@xyflow/react";
import {z} from "zod";
import {APIMethodEnum} from "@/api/models/common";

export const actionNodeBlueprint: Partial<Node<FlowNodeData>> = {
  type: FlowNodeTypes.ACTION,
  data: {
    type: FlowNodeTypes.ACTION,
    name: "Custom Action",
    settingsTitle: "Custom Action",
    description: "This is the action node",
    onChange: () => {
      console.log("Start node changed");
    },
    settings: [
      {
        title: "General",
        expandedSetting: true,
        params: [
          {
            hidden: true,
            type: SettingParamTypes.VALUE,
            key: "action",
            value: {},
          },
          {
            hidden: true,
            type: SettingParamTypes.VALUE,
            key: "name",
            value: "",
          },
          {
            label: "Endpoint",
            placeholder: "https://...",
            type: SettingParamTypes.ENDPOINT,
            key: "endpoint",
            validationSchema: z.object({
              url: z
                .string()
                .refine(
                  val => val === "" || /^https:\/\/[^/]+\/.+$/.test(val),
                  "URL must be a valid HTTPS URL",
                ),
              http_method: z.nativeEnum(APIMethodEnum),
            }),
            value: {
              url: "",
              http_method: "",
            },
          },
        ],
      },
      {
        title: "Headers",
        icon: "Settings2",
        description: "Specify the HTTP headers required for your API request.",
        params: [
          {
            type: SettingParamTypes.MULTIPLE,
            key: "headers",
            value: [],
            settings: [
              {
                placeholder: "Key",
                type: SettingParamTypes.INPUT,
                key: "key",
                value: "",
                validationSchema: z
                  .string()
                  .regex(
                    /^[A-Za-z0-9-]+$/,
                    "Invalid header key: only letters, digits, and hyphens allowed",
                  )
                  .min(1, "Key is required"),
              },
              {
                placeholder: "Value",
                type: SettingParamTypes.INPUT,
                key: "value",
                value: "",
                validationSchema: z.string().min(1, "Value is required"),
              },
            ],
          },
        ],
      },
      {
        title: "Authentication",
        icon: "KeyRound",
        params: [
          {
            type: SettingParamTypes.TABS,
            key: "authentication",
            value: {},
            tabs: [
              {
                title: "Custom",
                value: {},
                key: "custom",
                settings: [
                  {
                    label: "Auth Type",
                    type: SettingParamTypes.AUTHENTICATE,
                    key: "custom_auth",
                    value: {},
                  },
                ],
              },
              {
                title: "Hubspot",
                value: {},
                key: "hubspot",
                settings: [
                  {
                    type: SettingParamTypes.AUTHENTICATE,
                    key: "hubspot_auth",
                    value: {},
                  },
                ],
              },
              {
                title: "GoHighLevel",
                value: {},
                key: "ghl",
                settings: [
                  {
                    type: SettingParamTypes.AUTHENTICATE,
                    key: "ghl_auth",
                    value: {},
                  },
                ],
              },
              {
                title: "Salesforce",
                value: {},
                key: "salesforce",
                settings: [
                  {
                    type: SettingParamTypes.AUTHENTICATE,
                    key: "salesforce_auth",
                    value: {},
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Body and Variables",
        icon: "Braces",
        params: [
          {
            type: SettingParamTypes.TABS,
            key: "body_and_variables",
            value: {},
            tabs: [
              {
                title: "JSON",
                value: "",
                key: "json",
                settings: [
                  {
                    type: SettingParamTypes.JSON,
                    key: "body",
                    value: "",
                    validationSchema: z
                      .object({
                        selected: z.boolean().optional(),
                        body: z
                          .string()
                          .min(1, "Body is required")
                          .refine(val => {
                            try {
                              JSON.parse(val);
                              return true;
                            } catch {
                              return false;
                            }
                          }, "Body must be valid JSON"),
                      })
                      .optional(),
                  },
                ],
              },
              {
                title: "Query Parameters",
                value: [],
                key: "query_parameters",
                settings: [
                  {
                    type: SettingParamTypes.MULTIPLE,
                    key: "query_parameters",
                    value: [],
                    settings: [
                      {
                        type: SettingParamTypes.INPUT,
                        key: "key",
                        placeholder: "Key",
                        value: "",
                      },
                      {
                        type: SettingParamTypes.INPUT,
                        key: "value",
                        placeholder: "Value",
                        value: "",
                      },
                    ],
                    validationSchema: z.array(
                      z.object({
                        key: z.string().min(1, "param key is required"),
                        value: z.string().min(1, "param value is required"),
                      }),
                    ),
                  },
                ],
              },
              {
                title: "Variables",
                value: [],
                key: "variables",
                settings: [
                  {
                    type: SettingParamTypes.MULTIPLE,
                    key: "variables",
                    value: [],
                    settings: [
                      {
                        type: SettingParamTypes.INPUT,
                        key: "key",
                        placeholder: "Key",
                        value: "",
                      },
                      {
                        type: SettingParamTypes.SELECT,
                        key: "type",
                        placeholder: "Type",
                        value: "",
                        className: "w-[92px]",
                        selectOptions: [
                          {
                            name: "String",
                            value: "string",
                          },
                          {
                            name: "Int",
                            value: "int",
                          },
                          {
                            name: "Float",
                            value: "float",
                          },
                          {
                            name: "Boolean",
                            value: "bool",
                          },
                          {
                            name: "Array",
                            value: "list",
                          },
                          {
                            name: "Money",
                            value: "money",
                          },
                          {
                            name: "Phone Number",
                            value: "phone_number0",
                          },
                          {
                            name: "Email",
                            value: "email",
                          },
                          {
                            name: "Datetime",
                            value: "datetime",
                          },
                          {
                            name: "UTC Datetime",
                            value: "utc_datetime",
                          },
                          {
                            name: "Datetime Offset",
                            value: "datetime_offset",
                          },
                          {
                            name: "Date",
                            value: "date",
                          },
                        ],
                      },
                      {
                        type: SettingParamTypes.VALUE,
                        key: "description",
                        value: "",
                      },
                      {
                        type: SettingParamTypes.INPUT,
                        key: "value",
                        placeholder: "Value",
                        value: "",
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Messages",
        icon: "MessageCircle",
        params: [
          {
            label: "Let the Agent speak naturally",
            type: SettingParamTypes.SWITCH,
            key: "speak_naturally",
            value: undefined,
            validationSchema: z.boolean().optional(),
          },
          {
            label: "Start Message",
            placeholder: "Type start message...",
            type: SettingParamTypes.TEXTAREA,
            key: "start_message",
            validationSchema: z
              .string()
              .refine(val => !val || !/[*|~]/.test(val), {
                message: "Custom input cannot contain: * | ~",
              })
              .optional(),
            value: "",
          },
          {
            label: "Delay Message",
            placeholder: "Type delay message...",
            type: SettingParamTypes.TEXTAREA,
            key: "delay_message",
            value: "",
            validationSchema: z
              .string()
              .refine(val => !val || !/[*|~]/.test(val), {
                message: "Custom input cannot contain: * | ~",
              })
              .optional(),
          },
          {
            label: "Failure Message",
            placeholder: "Type failure message...",
            type: SettingParamTypes.TEXTAREA,
            key: "failure_message",
            value: "",
            validationSchema: z
              .string()
              .refine(val => !val || !/[*|~]/.test(val), {
                message: "Custom input cannot contain: * | ~",
              })
              .optional(),
          },
        ],
      },
      {
        title: "General Settings",
        icon: "Settings",
        description:
          "Explain the action's purpose, usage, and what to say while it’s running.git stat",
        params: [
          {
            label: "Action Name",
            placeholder: "New Custom Action",
            type: SettingParamTypes.INPUT,
            maxLength: 64,
            key: "name",
            value: "",
            validationSchema: z
              .string()
              .min(3, "Action name must be at least 3 characters.")
              .max(64, "Action name cannot be more than 64 characters.")
              .transform(val => val.replace(/ /g, "_"))
              .refine(val => /^(?!_)(?!.*_$)[A-Za-z0-9_]{3,64}$/.test(val), {
                message:
                  "Only letters, digits, underscores are allowed (3-64chars).",
              })
              .optional(),
          },
          {
            label: "Description",
            placeholder: "Call this API to do...",
            type: SettingParamTypes.TEXTAREA,
            key: "description",
            value: "",
            validationSchema: z
              .string()
              .min(1, "Action description is required")
              .max(128, "Action description must be less than 128 characters")
              .refine(val => !/[*|~]/.test(val), {
                message: "Custom input cannot contain: * | ~",
              })
              .optional(),
          },
        ],
      },
    ],
  },
  position: {x: 0, y: 0},
};
